/*
 * The reading view over `wiki/`.
 *
 * Three things carry the weight, and they are the three the Obsidian review reduced
 * that tool's load-bearing contribution to: a page tree with the rendered page and
 * its resolved links, a backlinks panel, and a filterable table over every page's
 * metadata. With those present there is nothing left to leave the app for.
 *
 * The metadata table is the one that pays for itself in a large base: "show me every
 * page whose source changed since it was written" is a question about a hundred
 * pages, not about one, and no amount of clicking through a tree answers it.
 *
 * `status` is a CHECK, not a judgement, and the filter labels say what each value
 * means: each page records the sha256 of every source it derives from plus the
 * conventions hash in force when it was written, so `stale` means a recorded hash no
 * longer matches and `orphaned` means a recorded source is gone.
 */
import { useMemo, useState } from 'react'
import {
  getWikiContent,
  WIKI_INDEX_PATH,
  type LinkGraph,
  type PageStatus,
  type WikiContent,
  type WikiIndex,
  type WikiPage,
} from '../../../api/knowledgeBases.ts'
import { AsyncState, Empty, Loaded } from '../components/AsyncState.tsx'
import { Markdown, resolveTarget } from '../components/Markdown.tsx'
import { ago, baseName, bytes, num } from '../lib/format.ts'
import { useResource, type Resource } from '../lib/useResource.ts'

interface WikiTabProps {
  kbId: string
  wiki: Resource<WikiIndex>
  links: Resource<LinkGraph>
}

interface StatusLook {
  label: string
  pill: string
  hint: string
}

const STATUS_LOOK: Record<PageStatus, StatusLook> = {
  fresh: {
    label: 'fresh',
    pill: 'pill--ok',
    hint: 'все записанные хэши источников и схемы совпадают',
  },
  stale: {
    label: 'stale',
    pill: 'pill--warn',
    hint: 'источник или конвенции изменились после того, как страница была написана',
  },
  orphaned: {
    label: 'orphaned',
    pill: 'pill--danger',
    hint: 'записанный источник исчез из raw/',
  },
  unverified: {
    label: 'unverified',
    pill: 'pill--neutral',
    hint: 'страница не записала провенанс — проверить нечего',
  },
}

/**
 * `status` is an open string on the wire, so an unfamiliar value renders as
 * itself with a neutral pill instead of being narrowed away or crashing a row.
 */
function lookFor(status: string): StatusLook | undefined {
  return (STATUS_LOOK as Partial<Record<string, StatusLook>>)[status]
}

type StatusFilter = PageStatus | 'all'

export function WikiTab({ kbId, wiki, links }: WikiTabProps) {
  const [selected, setSelected] = useState<string | null>(null)
  const [status, setStatus] = useState<StatusFilter>('all')
  const [needle, setNeedle] = useState('')
  const [view, setView] = useState<'read' | 'table'>('read')

  const pages = wiki.data?.pages ?? []
  const known = useMemo(() => new Set(pages.map((page) => page.path)), [pages])
  const path = selected ?? (known.has(WIKI_INDEX_PATH) ? WIKI_INDEX_PATH : (pages[0]?.path ?? null))

  const page = useResource<WikiContent>(`${kbId}:page:${path ?? 'none'}`, () =>
    path ? getWikiContent(kbId, path) : Promise.reject(new Error('страница не выбрана')),
  )

  const filtered = useMemo(() => {
    const search = needle.trim().toLowerCase()
    return pages.filter((candidate) => {
      if (status !== 'all' && candidate.status !== status) return false
      if (!search) return true
      return (
        candidate.path.toLowerCase().includes(search) ||
        candidate.title.toLowerCase().includes(search)
      )
    })
  }, [pages, status, needle])

  const backlinks = useMemo(() => {
    if (!path) return []
    const entry = links.data?.pages?.find((candidate) => candidate.path === path)
    return entry?.inbound ?? []
  }, [links.data, path])

  const orphanSet = useMemo(() => new Set(links.data?.orphans ?? []), [links.data])

  return (
    <div className="pane">
      <div className="filters" style={{ marginBottom: 14 }}>
        <div className="seg">
          <button type="button" aria-pressed={view === 'read'} onClick={() => setView('read')}>
            чтение
          </button>
          <button type="button" aria-pressed={view === 'table'} onClick={() => setView('table')}>
            таблица страниц
          </button>
        </div>
        <input
          className="input"
          value={needle}
          placeholder="Поиск по заголовку или пути"
          onChange={(event) => setNeedle(event.target.value)}
          style={{ minWidth: 220 }}
        />
        <div className="seg">
          {(['all', 'fresh', 'stale', 'orphaned', 'unverified'] as StatusFilter[]).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={status === value}
              title={value === 'all' ? 'Все страницы' : STATUS_LOOK[value].hint}
              onClick={() => setStatus(value)}
            >
              {value === 'all' ? 'все' : STATUS_LOOK[value].label}
            </button>
          ))}
        </div>
        <span className="muted-2" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
          {wiki.data
            ? `${num(filtered.length)} из ${num(wiki.data.page_count ?? pages.length)} · ${bytes(wiki.data.total_bytes ?? 0)}`
            : ''}
        </span>
      </div>

      <Loaded resource={wiki} what="страницы wiki" skeletonRows={6}>
        {(data) =>
          (data.pages ?? []).length === 0 ? (
            <Empty title="Wiki пока пуста">
              Страницы появятся после первого ingest: агент пишет их сам, по источникам из{' '}
              <code>raw/</code>.
            </Empty>
          ) : view === 'table' ? (
            <PageTable pages={filtered} orphans={orphanSet} onOpen={(value) => {
              setSelected(value)
              setView('read')
            }} />
          ) : (
            <div className="two-col">
              <div className="card">
                <div className="card__head">
                  <h3>
                    Страницы <span className="path">wiki/</span>
                  </h3>
                  <div className="right">
                    <span className="pill pill--accent pill--sm">пишет агент</span>
                  </div>
                </div>
                <div className="tree">
                  {filtered.length === 0 ? (
                    <p className="field__hint" style={{ padding: '4px 15px' }}>
                      Под фильтр ничего не подходит.
                    </p>
                  ) : (
                    filtered.map((candidate) => (
                      <button
                        key={candidate.path}
                        type="button"
                        className="tnode"
                        aria-current={candidate.path === path}
                        onClick={() => setSelected(candidate.path)}
                        title={lookFor(candidate.status)?.hint ?? candidate.status}
                      >
                        <span className="tnode__ind" style={{ width: indent(candidate.path) }} />
                        <span aria-hidden="true">▤</span>
                        <span className="tnode__name">{baseName(candidate.path)}</span>
                        {candidate.status !== 'fresh' ? (
                          <span
                            className={`tnode__tag pill ${lookFor(candidate.status)?.pill ?? 'pill--neutral'}`}
                          >
                            {lookFor(candidate.status)?.label ?? candidate.status}
                          </span>
                        ) : orphanSet.has(candidate.path) ? (
                          <span className="tnode__tag pill pill--danger">сирота</span>
                        ) : null}
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card__head">
                  <h3>{path ? baseName(path) : 'Страница'}</h3>
                  <div className="right">
                    {path ? <span className="path">{path}</span> : null}
                  </div>
                </div>

                {path === null ? (
                  <Empty title="Страница не выбрана" />
                ) : page.status !== 'ready' || page.data === null ? (
                  <AsyncState
                    what="страницу"
                    status={page.status}
                    error={page.error}
                    httpStatus={page.httpStatus}
                    onRetry={page.reload}
                    skeletonRows={6}
                  />
                ) : (
                  <>
                    <div className="viewer viewer--tall">
                      {Object.keys(page.data.frontmatter ?? {}).length > 0 ? (
                        <div className="frontmatter">
                          {Object.entries(page.data.frontmatter ?? {}).map(([field, value]) => (
                            <div key={field}>
                              <b>{field}:</b> {formatFrontmatterValue(value)}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      <Markdown
                        content={page.data.content}
                        known={known}
                        onOpenLink={(target) => {
                          const resolved = resolveTarget(target, known)
                          if (resolved) setSelected(resolved)
                        }}
                      />
                    </div>
                    <div className="backlinks">
                      <span className="kicker">
                        Входящие ссылки ·{' '}
                        {links.status === 'ready' ? num(backlinks.length) : '—'}
                      </span>
                      {links.status !== 'ready' ? (
                        <p className="field__hint" style={{ margin: '6px 0 0' }}>
                          Граф ссылок недоступен
                          {links.httpStatus ? ` · ${links.httpStatus}` : ''} — обратные ссылки
                          посчитать не из чего.
                        </p>
                      ) : backlinks.length === 0 ? (
                        <p className="field__hint" style={{ margin: '6px 0 0' }}>
                          На эту страницу никто не ссылается — это ровно то, что lint предложит либо
                          связать, либо архивировать.
                        </p>
                      ) : (
                        backlinks.map((source) => (
                          <button
                            key={source}
                            type="button"
                            className="backlink"
                            onClick={() => setSelected(source)}
                          >
                            {baseName(source)}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )
        }
      </Loaded>
    </div>
  )
}

function PageTable({
  pages,
  orphans,
  onOpen,
}: {
  pages: WikiPage[]
  orphans: Set<string>
  onOpen: (path: string) => void
}) {
  if (pages.length === 0) {
    return <Empty title="Под фильтр ничего не подходит" />
  }
  return (
    <div className="card">
      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>Страница</th>
              <th>Заголовок</th>
              <th>Статус</th>
              <th>Источников</th>
              <th>Строк</th>
              <th>Вес</th>
              <th>Обновлена</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((page) => {
              const look = lookFor(page.status)
              return (
                <tr key={page.path}>
                  <td className="k">
                    <button
                      type="button"
                      className="link-btn mono"
                      style={{ fontSize: 12 }}
                      onClick={() => onOpen(page.path)}
                    >
                      {page.path}
                    </button>
                  </td>
                  <td>{page.title}</td>
                  <td>
                    <span
                      className={`pill pill--sm ${look?.pill ?? 'pill--neutral'}`}
                      title={look?.hint ?? page.status}
                    >
                      {look?.label ?? page.status}
                    </span>
                    {orphans.has(page.path) ? (
                      <span className="pill pill--danger pill--sm" style={{ marginLeft: 5 }}>
                        сирота
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {(page.sources ?? []).length > 0 ? (
                      <span title={(page.sources ?? []).join('\n')}>
                        {num((page.sources ?? []).length)}
                      </span>
                    ) : (
                      <span className="muted-2">—</span>
                    )}
                  </td>
                  <td>{num(page.lines)}</td>
                  <td>{bytes(page.bytes)}</td>
                  <td>{ago(page.updated_at)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Nesting depth as an indent, so the flat page list still reads as a tree. */
function indent(path: string): number {
  const depth = Math.max(0, path.split('/').length - 2)
  return depth * 14
}

function formatFrontmatterValue(value: unknown): string {
  if (Array.isArray(value)) return value.map((entry) => String(entry)).join(', ')
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
